import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cliente } from '@/lib/cliente';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const MESES = 6;

/**
 * Crea en Proveedores los de Business Central con movimiento en los últimos meses que todavía no
 * están. Primero muestra la lista (vista previa) y solo crea al confirmar.
 */
export function ImportarProveedoresBCDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isFetching, error } = useQuery({
    queryKey: ['proveedores-importar-bc', MESES],
    enabled: open,
    gcTime: 0,
    queryFn: async () => {
      const r = await cliente.proveedores.importarDeBC(true, MESES);
      if (r.error) throw new Error(r.error.mensaje);
      return r.data;
    },
  });
  const importar = useMutation({
    mutationFn: async () => {
      const r = await cliente.proveedores.importarDeBC(false, MESES);
      if (r.error) throw new Error(r.error.mensaje);
      return r.data;
    },
    onSuccess: (r) => {
      toast.success(`Se crearon ${r?.creados ?? 0} proveedor(es). Falta agregarles la cuenta.`);
      qc.invalidateQueries({ queryKey: ['proveedores'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo importar'),
  });
  const candidatos = data?.candidatos ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importar proveedores de Business Central</DialogTitle>
          <DialogDescription>
            Proveedores con movimiento en los últimos {MESES} meses que todavía no están aquí. Se
            crean con el nombre y la cédula de BC y sin cuenta de banco (BC no la expone): después
            se la agrega cada uno, con el filtro "Sin cuenta".
          </DialogDescription>
        </DialogHeader>

        {isFetching && (
          <p className="text-sm text-muted-foreground">Consultando Business Central…</p>
        )}
        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
        {data && !isFetching && (
          <>
            <p className="text-sm">
              <strong>{candidatos.length}</strong> por crear
              {data.yaExistian > 0 && (
                <span className="text-muted-foreground">
                  {' '}
                  · {data.yaExistian} con movimiento ya estaban registrados
                </span>
              )}
              {candidatos.some((c) => !c.cedula) && (
                <span className="text-muted-foreground">
                  {' '}
                  · {candidatos.filter((c) => !c.cedula).length} sin cédula en BC
                </span>
              )}
            </p>
            {candidatos.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border text-sm">
                <table className="w-full">
                  <thead className="sticky top-0 bg-muted text-left text-xs">
                    <tr>
                      <th className="px-2 py-1">Código</th>
                      <th className="px-2 py-1">Nombre</th>
                      <th className="px-2 py-1">Cédula</th>
                      <th className="px-2 py-1">Último mov.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidatos.map((c) => (
                      <tr key={c.vendorCode} className="border-t">
                        <td className="px-2 py-1 font-mono text-xs">{c.vendorCode}</td>
                        <td className="px-2 py-1">{c.nombre}</td>
                        <td className="px-2 py-1">{c.cedula ?? '—'}</td>
                        <td className="px-2 py-1">{c.ultimoMovimiento ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importar.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => importar.mutate()}
            disabled={isFetching || candidatos.length === 0 || importar.isPending}
          >
            {importar.isPending ? 'Creando…' : `Crear ${candidatos.length} proveedor(es)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
